/**
 * useAgents – agent status + full analysis, with auto-refresh.
 */
import { create } from 'zustand'
import { getAgentStatus, postAgentAnalyze, getPortfolioSnapshot } from '@/lib/api'
import type {
  AgentStatus,
  AnalysisResult,
  UserProfile,
  PortfolioSnapshot,
} from '@/lib/types'

// ── Default profile ───────────────────────────────────────────────────────────
// Deliberately neutral: no fabricated net worth, age, or goal figures
// (real-data-only rule). `net_worth` is filled from the LIVE portfolio
// snapshot at analysis time in runAnalysis below; goals live in the backend
// household profile, not here.
export const DEFAULT_PROFILE: UserProfile = {
  user_id: 'unman_lai_001',
  name: 'Household',
  age: 0,
  role: '',
  net_worth: 0, // replaced with the live snapshot total at analysis time
  risk_tolerance: 'moderate',
  position_limit_gbp: 20_000, // mirrors MAX_POT_SIZE default enforced server-side
  goals: [],
}

interface AgentsState {
  agents: AgentStatus[]
  analysis: AnalysisResult | null
  loading: boolean
  analyzing: boolean
  error: string | null
  lastUpdated: string | null
  // Actions
  fetchStatus: () => Promise<void>
  runAnalysis: (profile?: UserProfile, snapshot?: PortfolioSnapshot) => Promise<void>
}

export const useAgents = create<AgentsState>()((set) => ({
  agents: [],
  analysis: null,
  loading: false,
  analyzing: false,
  error: null,
  lastUpdated: null,

  fetchStatus: async () => {
    set({ loading: true, error: null })
    try {
      const data = await getAgentStatus()
      set({ agents: data.agents, lastUpdated: data.timestamp, loading: false })
    } catch (e) {
      set({ error: String(e), loading: false })
    }
  },

  runAnalysis: async (profile = DEFAULT_PROFILE, snapshot?: PortfolioSnapshot) => {
    set({ analyzing: true, error: null })
    try {
      const snap = snapshot ?? (await getPortfolioSnapshot())
      // When using the default profile, fill net_worth from the LIVE snapshot
      // rather than any hardcoded figure.
      const liveProfile =
        profile === DEFAULT_PROFILE
          ? { ...profile, net_worth: snap?.total_value ?? 0 }
          : profile
      const analysis = await postAgentAnalyze(liveProfile, snap)
      set({ analysis, analyzing: false })
    } catch (e) {
      set({ error: String(e), analyzing: false })
    }
  },
}))
